import { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import { profileAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { User, Lock, KeyRound, Mail, Phone, Shield, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

export default function SettingsPage() {
  const { user, login, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');

  // Profile
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileDataLoading, setProfileDataLoading] = useState(true);

  // Change Password
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Change Username
  const [newUsername, setNewUsername] = useState('');
  const [usernamePassword, setUsernamePassword] = useState('');
  const [usernameLoading, setUsernameLoading] = useState(false);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const res = await profileAPI.get();
      const p = res.data.profile;
      setFullName(p.full_name || '');
      setEmail(p.email || '');
      setPhone(p.phone || '');
    } catch (err) {
      toast.error('Failed to load profile');
    } finally {
      setProfileDataLoading(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setProfileLoading(true);
    try {
      await profileAPI.update({ full_name: fullName, email, phone });
      // Update local auth state
      const updatedUser = { ...user, full_name: fullName };
      login(localStorage.getItem('token'), updatedUser);
      toast.success('Profile updated successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmNewPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (newPassword.length < 4) {
      toast.error('New password must be at least 4 characters');
      return;
    }
    setPasswordLoading(true);
    try {
      await profileAPI.changePassword({
        current_password: currentPassword,
        new_password: newPassword
      });
      toast.success('Password changed successfully!');
      setCurrentPassword(''); setNewPassword(''); setConfirmNewPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleChangeUsername = async (e) => {
    e.preventDefault();
    if (!newUsername.trim()) {
      toast.error('Enter a new username');
      return;
    }
    setUsernameLoading(true);
    try {
      const res = await profileAPI.changeUsername({
        new_username: newUsername,
        password: usernamePassword
      });
      // Update token and user data
      login(res.data.token, res.data.user);
      toast.success('Username changed successfully!');
      setNewUsername(''); setUsernamePassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change username');
    } finally {
      setUsernameLoading(false);
    }
  };

  if (profileDataLoading) {
    return <div className="page-container"><p>Loading settings...</p></div>;
  }

  return (
    <div className="page-container">
      <h2 className="text-2xl font-semibold tracking-tight mb-6" style={{ fontFamily: 'Space Grotesk' }}>Settings</h2>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="profile" data-testid="settings-profile-tab">
            <User className="h-4 w-4 mr-2" /> Profile
          </TabsTrigger>
          <TabsTrigger value="security" data-testid="settings-security-tab">
            <Lock className="h-4 w-4 mr-2" /> Security
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <Card className="max-w-2xl">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                  <User className="h-5 w-5 text-[#0B5CAB]" />
                </div>
                <div>
                  <CardTitle className="text-lg" style={{ fontFamily: 'Space Grotesk' }}>Personal Information</CardTitle>
                  <p className="text-sm text-muted-foreground">Update your profile details</p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUpdateProfile} className="space-y-4">
                <div className="space-y-2">
                  <Label>Full Name</Label>
                  <Input data-testid="settings-fullname-input" placeholder="John Doe" value={fullName} onChange={e => setFullName(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input data-testid="settings-email-input" className="pl-10" placeholder="john@example.com" value={email} onChange={e => setEmail(e.target.value)} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Phone Number</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input data-testid="settings-phone-input" className="pl-10" placeholder="(555) 123-4567" value={phone} onChange={e => setPhone(e.target.value)} />
                  </div>
                </div>
                <div className="pt-2">
                  <Button data-testid="settings-save-profile-button" type="submit" disabled={profileLoading}>
                    {profileLoading ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security">
          <div className="max-w-2xl space-y-6">
            {/* Change Username */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
                    <KeyRound className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg" style={{ fontFamily: 'Space Grotesk' }}>Change Username</CardTitle>
                    <p className="text-sm text-muted-foreground">Current: <span className="font-medium">@{user?.username}</span></p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleChangeUsername} className="space-y-4">
                  <div className="space-y-2">
                    <Label>New Username</Label>
                    <Input data-testid="settings-new-username-input" placeholder="Choose a new username" value={newUsername} onChange={e => setNewUsername(e.target.value)} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Confirm with Password</Label>
                    <Input data-testid="settings-username-password-input" type="password" placeholder="Enter your current password" value={usernamePassword} onChange={e => setUsernamePassword(e.target.value)} required />
                  </div>
                  <Button data-testid="settings-change-username-button" type="submit" disabled={usernameLoading}>
                    {usernameLoading ? 'Changing...' : 'Change Username'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Change Password */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
                    <Shield className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg" style={{ fontFamily: 'Space Grotesk' }}>Change Password</CardTitle>
                    <p className="text-sm text-muted-foreground">Keep your account secure</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleChangePassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Current Password</Label>
                    <Input data-testid="settings-current-password-input" type="password" placeholder="Enter current password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} required />
                  </div>
                  <div className="space-y-2">
                    <Label>New Password</Label>
                    <Input data-testid="settings-new-password-input" type="password" placeholder="Enter new password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Confirm New Password</Label>
                    <Input data-testid="settings-confirm-new-password-input" type="password" placeholder="Confirm new password" value={confirmNewPassword} onChange={e => setConfirmNewPassword(e.target.value)} required />
                  </div>
                  <Button data-testid="settings-change-password-button" type="submit" disabled={passwordLoading}>
                    {passwordLoading ? 'Changing...' : 'Change Password'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Account Info */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  <p className="text-sm font-medium">Account Security</p>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-green-50">
                    <span className="text-sm">Password Protection</span>
                    <Badge className="bg-green-100 text-green-700 border-green-200">Active</Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-green-50">
                    <span className="text-sm">256-bit SSL Encryption</span>
                    <Badge className="bg-green-100 text-green-700 border-green-200">Active</Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-green-50">
                    <span className="text-sm">FDIC Insured</span>
                    <Badge className="bg-green-100 text-green-700 border-green-200">Active</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
